import { useState } from "react";
import { useSignExtraContract } from "@/hooks/use-extra-form-token";
import type { ExtraFormContractData } from "@/hooks/use-extra-form-token";

interface Props {
  token: string;
  contract: ExtraFormContractData;
  projectName: string;
  onSigned: () => void;
}

/**
 * VIB-1282: kontrakt-visning + signeringssteg i selvbetjeningsskjemaet.
 * Skilt ut fra ExtraFormPage (som ellers ville passert ~400 linjer med to
 * fulle stegvisninger i samme fil).
 *
 * Forenkling (dokumentert i PR): datafelter (adresse/personnr/kontonr) er
 * allerede komplette når PL sender en kontrakt til signering — generate-extra-contract
 * nekter å generere en kontrakt med manglende obligatoriske felt. Denne
 * siden viser derfor KUN kontrakt + signatur, ikke et kombinert skjema+signer-steg.
 */
export default function ContractSignStep({ token, contract, projectName, onSigned }: Props) {
  const [signedName, setSignedName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const signMut = useSignExtraContract();

  const handleSign = (e: React.FormEvent) => {
    e.preventDefault();
    if (!signedName.trim() || !confirmed) return;
    setErrorMsg("");
    signMut.mutate(
      { token, signed_name: signedName.trim() },
      { onSuccess: onSigned, onError: (err) => setErrorMsg(err.message) },
    );
  };

  return (
    <form onSubmit={handleSign} className="bg-white rounded-xl shadow-sm border p-6 space-y-5">
      {projectName && (
        <p className="text-sm text-gray-500 text-center pb-2 border-b">
          Prosjekt: <span className="font-medium text-gray-700">{projectName}</span>
        </p>
      )}

      <div>
        <p className="text-sm font-medium text-gray-700 mb-1">{contract.title}</p>
        {contract.pdf_url ? (
          <a
            href={contract.pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[var(--sr-reveblaa)] hover:underline"
          >
            Åpne kontrakten for gjennomlesing →
          </a>
        ) : (
          <p className="text-sm text-red-600">Kunne ikke laste kontrakten. Kontakt Sølvrev.</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Fullt navn (signatur) <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          required
          className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sr-reveblaa)] focus:border-transparent"
          value={signedName}
          onChange={(e) => setSignedName(e.target.value)}
        />
      </div>

      <label className="flex items-start gap-2 text-xs text-gray-600">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        <span>
          Jeg har lest kontrakten og bekrefter at jeg signerer den med navnet over. Tidspunkt, IP-adresse og en
          hash av dokumentet lagres som signaturbevis (enkel elektronisk signatur).
        </span>
      </label>

      {errorMsg && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{errorMsg}</p>}

      <button
        type="submit"
        disabled={signMut.isPending || !signedName.trim() || !confirmed}
        className="w-full h-11 bg-[var(--sr-reveblaa)] hover:opacity-90 disabled:opacity-50 text-white font-medium rounded-lg text-sm transition-opacity"
      >
        {signMut.isPending ? "Signerer..." : "Signer kontrakt"}
      </button>
    </form>
  );
}
